/* eslint-disable max-lines */
import {
  DownCircleOutlined,
  FilterOutlined,
  HighlightOutlined,
  LinkOutlined,
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons'
import type { ViewFieldsFragment } from '@salescore/client-api'
import { HUB_PROPERTY_TYPE_ICONS } from '@salescore/client-common'
import type {
  CompiledFormViewConfig,
  CompiledKpiPivotViewConfig,
  CompiledKpiViewConfig,
  CompiledSheetViewConfig,
  ViewQueryList,
  ViewQueryRecordNode,
  ViewQueryRecordNodeWithId,
} from '@salescore/core'
import { App, Space, Tooltip } from 'antd'
import type { HashMap } from 'effect'
import { type ReactElement, useCallback, useMemo, useState } from 'react'

import type { RSheetRecordNode, RSheetsNodeBlockCell, SheetColumn, SheetSystemColumn } from '../../domain/column'
import type { RSheetsCursorPosition } from '../../domain/cursor/cursorTypes.ts'
import { getHeaderCompactLevel, type HeaderCompactLevel, type SheetTableContext } from '../../recoil/context'
import { useGetInnerCells, useRowExpandable } from '../../recoil/cursor.ts'
import { useCalculatedRowHeight, useSetRowConfig } from '../../recoil/navigation/hooks.ts'
import type { LatestFieldChangeItem } from '../../recoil/recordsHistory.ts'
import type { SalescoreObjectAbility, ViewAbility } from '../../recoil/view/atoms'
import { useResizeColumnMutation } from '../../recoil/viewConfig.ts'
import {
  CELL_BODY_PADDING_LEFT,
  HEADER_CELL_HORIZONTAL_PADDING,
  MINIMUM_WIDTH,
  SHEET_HEADER_CELL_HEIGHT,
} from '../../rsheet/components/body/const.tsx'
import { RSheetsFixedFooterRow } from '../../rsheet/components/footer/RSheetsFixedFooterRow'
import { RSheetsFooterRow } from '../../rsheet/components/footer/RSheetsFooterRow'
import { DraggableBar } from '../../rsheet/components/header/DraggableBar.tsx'
import { DraggableHeaderColumn } from '../../rsheet/components/header/DraggableHeaderColumn.tsx'
import { SheetHeaderContextMenu } from '../../rsheet/components/header/HeaderContextMenu.tsx'
import {
  type BlockHeaderStyle,
  DEFAULT_HEADER_COLOR,
  getHeaderColor,
} from '../../rsheet/components/header/RSheetsNodeBlockHeader.tsx'
import { RSheet } from '../../rsheet/components/RSheet'
import {
  type SetMainCursorByRecordIdArguments,
  useSetMainCursorByRecordId,
} from '../../rsheet/hooks/useSetMainCursorByRecordId.ts'
import { RSheetsStyle } from '../../rsheet/util/RSheetsStyle'
import { SheetTableCellGroup } from './ViewUISheet/SheetTableCellGroup.tsx'
import { buildViewQueryNodeDetailModalProperties } from './ViewUISheetWithNavigation/SheetNavigation/ViewQueryNodeDetailModal.tsx'

export const ViewUISheetBody = ({
  context,
  columns,
  compiledConfig,
  view,
  ability,
  query,
  setHasChangeToViewSchema,
}: {
  context: SheetTableContext
  columns: Array<SheetSystemColumn<ViewQueryRecordNode> | SheetColumn<ViewQueryRecordNode>>
  compiledConfig: CompiledSheetViewConfig
  view: ViewFieldsFragment
  ability: ViewAbility & SalescoreObjectAbility
  query: ViewQueryList
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ReactElement => {
  const setRowConfig = useSetRowConfig()
  const rowHeight = useCalculatedRowHeight(compiledConfig)
  const visibleRowNumber = compiledConfig.ui[0].visibleRowNum
  const getInnerCells = useGetInnerCells(visibleRowNumber)
  const setMainCursorByRecordId = useSetMainCursorByRecordId(
    context.fixedLeftColumnIndex,
    columns,
    compiledConfig,
    ability,
    query,
    visibleRowNumber,
  )
  const rowExpandable = useRowExpandable(columns, visibleRowNumber)
  const { modal } = App.useApp()

  const fixedFooter = useCallback(
    (
      leftFixedColumns: Array<SheetSystemColumn<ViewQueryRecordNode> | SheetColumn<ViewQueryRecordNode>>,
      notFixedColumns: Array<SheetSystemColumn<ViewQueryRecordNode> | SheetColumn<ViewQueryRecordNode>>,
    ): ReactElement => (
      <div className="sticky bottom-0 flex" style={{ zIndex: RSheetsStyle.zIndex.fixedFooterRowWhenLeftFixed }}>
        <RSheetsFixedFooterRow
          leftFixed={true}
          isGoalView={context.isGoalView}
          columns={columns}
          targetColumns={leftFixedColumns}
          compiledConfig={compiledConfig}
          view={view}
          ability={ability}
          setHasChangeToViewSchema={setHasChangeToViewSchema}
        />
        <RSheetsFixedFooterRow
          leftFixed={false}
          isGoalView={context.isGoalView}
          columns={columns}
          targetColumns={notFixedColumns}
          compiledConfig={compiledConfig}
          view={view}
          ability={ability}
          setHasChangeToViewSchema={setHasChangeToViewSchema}
        />
      </div>
    ),
    [ability, columns, compiledConfig, context.isGoalView, view],
  )
  const footer = useCallback(
    (totalWidth: number): ReactElement => (
      <RSheetsFooterRow totalWidth={totalWidth} columns={columns} view={view} ability={ability} query={query} />
    ),
    [ability, columns, query, view],
  )
  const renderHeaderCell = useCallback(
    (column: SheetSystemColumn<ViewQueryRecordNode> | SheetColumn<ViewQueryRecordNode>, columnIndex: number) =>
      column.columnType === 'sheet_system' ? (
        <SheetSystemHeaderCell
          columnIndex={columnIndex}
          column={column}
          columns={columns}
          compiledConfig={compiledConfig}
          view={view}
          setHasChangeToViewSchema={setHasChangeToViewSchema}
        />
      ) : (
        <SheetHeaderCell
          columnIndex={columnIndex}
          column={column}
          columns={columns}
          context={context}
          compiledConfig={compiledConfig}
          view={view}
          query={query}
          setHasChangeToViewSchema={setHasChangeToViewSchema}
        />
      ),
    [columns, context, compiledConfig, view],
  )

  const renderCellLabel = useCallback(
    (cell: RSheetsNodeBlockCell) => (
      <button
        type="button"
        className="inline h-auto min-w-0 border-0 bg-transparent p-0 text-inherit shadow-none outline-none ring-0 hover:!bg-transparent hover:!text-inherit focus:!bg-transparent focus:!text-inherit focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
        style={{ font: 'inherit', lineHeight: 'inherit', cursor: 'pointer' }}
        onClick={() => {
          const nodeName = cell.block.nodePath.at(-1)
          if (nodeName !== undefined) {
            const m = modal.confirm(
              buildViewQueryNodeDetailModalProperties({
                nodeName,
                onAfterFinish: () => {
                  m.destroy()
                },
                compiledConfig,
                query,
                setHasChangeToViewSchema,
              }),
            )
          }
        }}
      >
        {cell.label}
      </button>
    ),
    [compiledConfig, modal],
  )
  const blockHeaderStyle = useMemo(
    (): BlockHeaderStyle => ({
      treeHeaderHeightPerNode: 20,
      blockStyle: {},
      renderCellLabel,
    }),
    [renderCellLabel],
  )

  const renderCellGroup = useCallback(
    ({
      row,
      column,
      rowIndex,
      columnIndex,
      expandable,
      expanded,
      latestFieldChangesRow,
      setDragging,
      setMainCursorByRecordId,
      expandOnDrag,
    }: {
      row: ViewQueryRecordNodeWithId
      column: SheetSystemColumn<ViewQueryRecordNode> | SheetColumn<ViewQueryRecordNode>
      rowIndex: number
      columnIndex: number
      expandable: boolean
      expanded: boolean
      latestFieldChangesRow: HashMap.HashMap<string, LatestFieldChangeItem>
      setMainCursorByRecordId: (arguments_: SetMainCursorByRecordIdArguments) => void
      setDragging: () => void
      expandOnDrag: (position: RSheetsCursorPosition) => void
    }): ReactElement => (
      <SheetTableCellGroup
        row={row}
        column={column}
        rowIndex={rowIndex}
        columnIndex={columnIndex}
        getInnerCells={getInnerCells}
        expandable={expandable}
        expanded={expanded}
        latestFieldChangesRow={latestFieldChangesRow}
        rowHeight={rowHeight}
        setMainCursorByRecordId={setMainCursorByRecordId}
        setRowConfig={setRowConfig}
        view={view}
        setDragging={setDragging}
        expandOnDrag={expandOnDrag}
        columns={columns}
        rootRecordNode={row}
        query={query}
        ability={ability}
      />
    ),
    [ability, columns, getInnerCells, query, rowHeight, setRowConfig, view],
  )
  return (
    <RSheet
      height="100%"
      columns={columns}
      context={context}
      fixedFooter={fixedFooter}
      footer={footer}
      columnVirtualization={true}
      compiledConfig={compiledConfig}
      renderHeaderCell={renderHeaderCell}
      view={view}
      blockHeaderStyle={blockHeaderStyle}
      visibleRowNumber={visibleRowNumber}
      rowExpandable={rowExpandable}
      renderCellGroup={renderCellGroup}
      setMainCursorByRecordId={setMainCursorByRecordId}
      rowHeight={rowHeight}
      ability={ability}
      query={query}
      setHasChangeToViewSchema={setHasChangeToViewSchema}
    />
  )
}

const SheetSystemHeaderCell = ({
  column,
  columnIndex,
  columns,
  compiledConfig,
  view,
  setHasChangeToViewSchema,
}: {
  column: SheetSystemColumn<ViewQueryRecordNode>
  columnIndex: number
  columns: Array<SheetSystemColumn<ViewQueryRecordNode> | SheetColumn<ViewQueryRecordNode>>
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig
  view: ViewFieldsFragment
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ReactElement => {
  const resizeColumnMutation = useResizeColumnMutation(compiledConfig, view)
  const { width } = column
  const [openColumnContextMenu, setOpenColumnContextMenu] = useState(false)

  return (
    <div
      onDragOver={(event) => {
        event.preventDefault() // onDropを発火させるのに必要
      }}
      onDrop={(event) => {
        // onDropで特にロジックは書かないが、onDropの定義はドロップアニメーション(要素の元の位置に戻るアニメーション)をdisableするために必要
        event.preventDefault()
      }}
      style={{
        width,
        position: 'relative',
        transition: RSheetsStyle.cellTransition,
      }}
    >
      <DraggableHeaderColumn
        width={width}
        height={SHEET_HEADER_CELL_HEIGHT}
        widths={columns.map((x) => x.width)}
        column={column}
        columnIndex={columnIndex}
        onClick={() => {
          setOpenColumnContextMenu(true)
        }}
        onContextMenu={() => {
          setOpenColumnContextMenu(true)
        }}
        columns={columns}
        compiledConfig={compiledConfig}
        view={view}
        setHasChangeToViewSchema={setHasChangeToViewSchema}
      />
      <div
        className="p-1"
        style={{
          borderRight: `1px ${DEFAULT_HEADER_COLOR.borderColor} solid`,
          color: openColumnContextMenu ? DEFAULT_HEADER_COLOR.textColorWhite : DEFAULT_HEADER_COLOR.textColor,
          backgroundColor: openColumnContextMenu ? DEFAULT_HEADER_COLOR.bgColorDark : DEFAULT_HEADER_COLOR.bgColor,
          padding: `0px ${HEADER_CELL_HORIZONTAL_PADDING}px`,
          width: '100%',
          position: 'relative',
          overflow: 'hidden',
          height: SHEET_HEADER_CELL_HEIGHT - 2,
          display: 'flex',
          alignItems: 'center',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        }}
      />
      <DraggableBar
        width={width}
        height={SHEET_HEADER_CELL_HEIGHT}
        setWidth={(width) => {
          resizeColumnMutation({ column, width, allColumnIndex: columnIndex, setHasChangeToViewSchema })
        }}
      />
    </div>
  )
}

const SheetHeaderCell = ({
  column,
  columnIndex,
  columns,
  context,
  compiledConfig,
  view,
  query,
  setHasChangeToViewSchema,
}: {
  column: SheetColumn<ViewQueryRecordNode>
  columnIndex: number
  columns: Array<SheetSystemColumn<ViewQueryRecordNode> | SheetColumn<ViewQueryRecordNode>>
  context: SheetTableContext
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig
  view: ViewFieldsFragment
  query: ViewQueryList
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ReactElement => {
  const resizeColumnMutation = useResizeColumnMutation(compiledConfig, view)
  const { width } = column
  const displayTitle = `${column.title}${column.required ? '*' : ''}`
  const compactLevel = getHeaderCompactLevel(width, displayTitle, column)
  const isCompact = compactLevel === 'compact'
  const [openColumnContextMenu, setOpenColumnContextMenu] = useState(false)
  const [headerFocused, setHeaderFocused] = useState(false)

  // Tooltipの領域がコンテキストメニューまで含んでおり、コンテキストメニューを開いた際にフォーカスが残ったままになってしまうこと防ぐ
  const tooltipVisibility =
    column.helpText !== undefined && column.helpText !== '' && headerFocused && !openColumnContextMenu

  return (
    <Tooltip title={column.helpText} placement="right" open={tooltipVisibility}>
      <div
        onMouseEnter={() => {
          setHeaderFocused(true)
        }}
        onMouseLeave={() => {
          setHeaderFocused(false)
        }}
        onDragOver={(event) => {
          event.preventDefault() // onDropを発火させるのに必要
        }}
        onDrop={(event) => {
          // onDropで特にロジックは書かないが、onDropの定義はドロップアニメーション(要素の元の位置に戻るアニメーション)をdisableするために必要
          event.preventDefault()
        }}
        style={{
          width,
          position: 'relative',
          transition: RSheetsStyle.cellTransition,
        }}
      >
        <DraggableHeaderColumn
          width={width}
          height={SHEET_HEADER_CELL_HEIGHT}
          widths={columns.map((x) => x.width)}
          column={column}
          columnIndex={columnIndex}
          onClick={() => {
            setOpenColumnContextMenu(true)
            setHeaderFocused(false)
          }}
          onContextMenu={() => {
            setOpenColumnContextMenu(true)
            setHeaderFocused(false)
          }}
          columns={columns}
          compiledConfig={compiledConfig}
          view={view}
          setHasChangeToViewSchema={setHasChangeToViewSchema}
        />
        <div
          className="p-1"
          style={{
            borderRight: `1px ${getHeaderColor(column.color).borderColor} solid`,
            color: openColumnContextMenu
              ? getHeaderColor(column.color).textColorWhite
              : getHeaderColor(column.color).textColor,
            backgroundColor: openColumnContextMenu
              ? getHeaderColor(column.color).bgColorDark
              : getHeaderColor(column.color).bgColor,
            padding: isCompact
              ? `0px ${HEADER_CELL_HORIZONTAL_PADDING / 2}px`
              : `0px ${HEADER_CELL_HORIZONTAL_PADDING}px`,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            height: SHEET_HEADER_CELL_HEIGHT - 2,
            display: 'flex',
            alignItems: 'center',
            ...(isCompact ? {} : { whiteSpace: 'nowrap' as const, textOverflow: 'ellipsis' }),
          }}
        >
          <SheetHeaderCellBody column={column} compactLevel={compactLevel} />
        </div>
        <DraggableBar
          width={width}
          height={SHEET_HEADER_CELL_HEIGHT}
          setWidth={(width) => {
            resizeColumnMutation({ column, width, allColumnIndex: columnIndex, setHasChangeToViewSchema })
          }}
        />
        {openColumnContextMenu && (
          <SheetHeaderContextMenu
            context={context}
            column={column}
            hide={() => {
              setOpenColumnContextMenu(false)
              setHeaderFocused(false)
            }}
            columns={columns}
            compiledConfig={compiledConfig}
            view={view}
            query={query}
            setHasChangeToViewSchema={setHasChangeToViewSchema}
          />
        )}
      </div>
    </Tooltip>
  )
}

const SheetHeaderCellBody = ({
  column,
  compactLevel,
}: {
  column: SheetColumn<ViewQueryRecordNode>
  compactLevel: HeaderCompactLevel
}): ReactElement => {
  if (column.metaType === 'record_url') {
    return <></>
  }
  if (column.width === MINIMUM_WIDTH) {
    return <></>
  }

  return (
    <Space
      style={{
        paddingLeft: CELL_BODY_PADDING_LEFT,
        ...(compactLevel === 'compact' ? { fontSize: 12, lineHeight: 1.3 } : {}),
      }}
    >
      <SheetColumnIcon column={column} />
      <span className="whitespace-normal break-all font-bold">
        {column.title}
        {column.required ? '*' : ''}
      </span>
      {column.readonly && <LockOutlined style={{ opacity: 0.5 }} />}
      {column.isFiltered && <FilterOutlined style={{ opacity: 0.5 }} />}
      {column.sortState === 'asc' ? (
        <SortAscendingOutlined />
      ) : // eslint-disable-next-line sonarjs/no-nested-conditional
      column.sortState === 'desc' ? (
        <SortDescendingOutlined />
      ) : (
        <></>
      )}
      {column.isHighlighted && <HighlightOutlined style={{ opacity: 0.5 }} />}
    </Space>
  )
}

const SheetColumnIcon = ({ column }: { column: SheetColumn<RSheetRecordNode> }): ReactElement => {
  switch (column.metaType) {
    case 'relation': {
      return <SearchOutlined />
    }
    case 'url': {
      return <LinkOutlined />
    }
    case 'phone_number': {
      return <PhoneOutlined />
    }
    case 'email': {
      return <MailOutlined />
    }
    case 'select': {
      return <DownCircleOutlined />
    }
    case 'multi_select': {
      return <UnorderedListOutlined />
    }
    default: {
      break
    }
  }
  if (column.type === 'notFound') {
    return <QuestionCircleOutlined />
  }

  return <>{HUB_PROPERTY_TYPE_ICONS[column.type]}</>
}
